/**
 * Run `fn` once, on the first user interaction or when the browser goes idle
 * after `load` — whichever comes first. Used to keep analytics bundles
 * (gtag, PostHog session recorder) off the critical rendering path.
 */
export function runWhenIdleOrInteractive(fn: () => void, idleTimeoutMs = 5000): void {
  if (typeof window === 'undefined') return

  let done = false
  const events: (keyof WindowEventMap)[] = ['pointerdown', 'keydown', 'touchstart', 'scroll']

  const run = () => {
    if (done) return
    done = true
    events.forEach((e) => window.removeEventListener(e, run))
    fn()
  }

  events.forEach((e) => window.addEventListener(e, run, { passive: true }))

  const scheduleIdle = () => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run, { timeout: idleTimeoutMs })
    } else {
      setTimeout(run, idleTimeoutMs)
    }
  }

  if (document.readyState === 'complete') {
    scheduleIdle()
  } else {
    window.addEventListener('load', scheduleIdle, { once: true })
  }
}
